
 import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Heading from "../../components/ui/Heading"; 

// StatCard Component
const StatCard = ({ title, value, icon, color }) => (
  <div className={`border-l-4 ${color} bg-white p-4 shadow rounded-none sm:rounded-xl`}>
    <div className="flex items-center">
      <div className="mr-4">{icon}</div>
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-semibold text-gray-800">{value}</p>
      </div>
    </div>
  </div>
);

// StatTable Component
const StatTable = ({ title, data, type }) => {
  const formatCurrency = (value) =>
    (value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  return (
    <div className="mt-4 overflow-hidden bg-white rounded-none shadow-md sm:rounded-xl sm:mt-8">
      <h3
        className={`text-xl font-semibold p-4 text-white ${
          type === 'po' ? 'bg-blue-500' : 'bg-green-500'
        }`}
      >
        {title}
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                Period
              </th>
              <th className="px-4 py-3 text-xs font-medium text-right text-gray-500 uppercase">
                {type === 'po' ? 'PO Qty' : 'Inv Qty'}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-right text-gray-500 uppercase">
                {type === 'po' ? 'Order Total' : 'Inv Value'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                  {item.Orders || item.Sales}
                </td>
                <td className="px-4 py-4 text-sm text-right text-gray-500 whitespace-nowrap">
                  {item.PoQty || item.InvQty || 'N/A'}
                </td>
                <td className="px-4 py-4 text-sm text-right text-gray-500 whitespace-nowrap">
                  {formatCurrency(item.OrderTotal || item.InvValue) || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ po: [], sales: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .getSalesPoStatement(user.UsrName)
      .then((data) =>
        setStats({ po: data.Table || [], sales: data.Table1 || [] })
      )
      .catch(() => setError('Failed to fetch dashboard data.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  const formatCurrency = (value) =>
    (value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const todaySales = stats.sales.find((s) => s.Sales === 'Today');
  const todayPOs = stats.po.find((p) => p.Orders === 'Today');
  const monthSales = stats.sales.find((s) => s.Sales === 'ThisMonth');
  const monthPOs = stats.po.find((p) => p.Orders === 'ThisMonth');

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  return (
    <div className="w-full min-h-screen bg-gray-100">
      <div className="w-full p-0 mx-auto max-w-7xl sm:p-8">
      <Heading>Business Overview</Heading>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-10">
          <StatCard
            title="Today's Sales"
            value={formatCurrency(todaySales?.InvValue)}
            color="border-green-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
          <StatCard
            title="Today's POs"
            value={formatCurrency(todayPOs?.OrderTotal)}
            color="border-blue-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
          />
          <StatCard
            title="This Month's Sales"
            value={formatCurrency(monthSales?.InvValue)}
            color="border-green-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            }
          />
          <StatCard
            title="This Month's POs"
            value={formatCurrency(monthPOs?.OrderTotal)}
            color="border-blue-500"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 sm:gap-8">
          <StatTable title="Sales Breakdown" data={stats.sales} type="sales" />
          <StatTable title="PO Breakdown" data={stats.po} type="po" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


